/* Dynamic Plot code copyright 2020 (c) Tuva Labs Inc. */

export const wrapDynCaseSampler = function(Dyn) {
    let mediator = Dyn.mediator;

    /** information about the population used in the dataset.
     *  A 'population' is displayed as filtered cases of the original dataset.
     *  IMPORTANT: our .caseIndices here are ODI: original (pre-filter) dataset case indices
     */
    class PopulationTracker {
        constructor() {
            this.reset();
        }
        reset() {
            this.caseIndices = [];   // ODI: saved post-filter case indices to sample from
            this.numCasesPreFilter = 0;
            this.numCasesAfterFilter = 0;
            this.savedFilter = null; // to restore population filtering when sampling is cleared
            this.savedPlotTitle = "";
        }
    }

    /** info about the sample currently being used in the dataset.
     *  IMPORTANT: our .caseIndices here are ODI: original (pre-filter) dataset case indices
     */
    class SampleTracker {
        constructor() {
            this.reset();
        }
        reset() {
            this.setIndex = -1;       // which set of samples is being viewed?
            this.sampleIndex = -1;    // which sample in this.sampledObjs[] is being viewed?\
            this.caseIndices = [];    // array of random case indices to include (was this.sampledCaseIndices)
            this.numCasesPreFilter = 0; // see also dataSet.getNumCasesPreFilter() and dataSet.getNumCasesAfterFilter()
        }
    }

    /**
     *  info about the attributes that make the distribution data set.
     *  IMPORTANT: case values of the distribution do not reference the original dataset at all.
     */
    class DistributionTracker {
        constructor() {
            this.reset();
        }
        reset() {
            this.attributes = []; // attribute data, one attribute per measure, one case per sample in sampleSets
            this.numCasesInDistribution = 0; // coordinated with .attributes change
            this.distSerialNum = 0; // unique ID, so clients know if they have the same distribution data or not
        }
    }

    class CaseSampler {
        /**
         * Constructor for a random sampling manger for the data set.
         * Its job is to accept commands to sample cases from the original (population) dataSet.
         * And modify the dataSet + attributes so sampled cases can be plotted / calculated.
         *
         * DATASET layers:
         *  (0) original raw unmodified data (dataSet.attribs[*].originalCases)
         *  (1) post-data-type-transformed data (e.g. convert date strings to numbers, etc)
         *  (2) post-filter population filter (population has possibly fewer cases than original data)
         *  (3) post-Sampling case data (pre-filter)
         *  (4) post-Sampling-filter case data
         *
         * @param aDataSet
         * @constructor
         */
        constructor ( aDataSet ) {

            // References
            this.dataSet = aDataSet; 	// reference to parent object.

            // Sample, Population, and Sample Distribution data
            this.pop = new PopulationTracker();
            this.sample = new SampleTracker();
            this.dist = new DistributionTracker();
            this.sampleSets = [];       // array of sample sets, each with an array of samples of the population.
            this.samplerSerialNum = 0;  // unique ID for each sampler configuration (0+), so clients know if the have the current sample data or not
            this.lastGenerate = {};     // {*} saved data from last press of Generate in panel, to restore for next sampling session.
            this.selectedView = "";   // "viewSample", "viewDistribution", "viewPopulation"
            this.taskMaster = new Dyn.SamplerTaskMaster( this );
        }

        get allowChangeToAttributeNames() { return true; } // set to TRUE for experimental feature to change attribute names when viewing distributions.
                                           // e.g. body weight => sample mean of body weight
        get debug() { return false; } // Generate extra debugging messages?

        save(){
            var samplerModel = {};

            Dyn.assert(samplerModel.sample.caseIndices && samplerModel.sampleSets);
            samplerModel.pop = Dyn.dataUtil.clone(this.pop);
            samplerModel.sample = Dyn.dataUtil.clone(this.sample);
            samplerModel.sampleSets = Dyn.dataUtil.clone(this.sampleSets);
            samplerModel.lastGenerate = Dyn.dataUtil.clone(this.lastGenerate);

            return samplerModel;
        }

        restore( savedData ) {
            var hasSample = false;

            // restore filter data
            if( savedData.sampleSets.length > 0 /* similar to this.hasSample() */ ){
                this.pop = Dyn.dataUtil.clone(savedData.pop);
                this.sample = Dyn.dataUtil.clone(savedData.sample);
                this.sampleSets = Dyn.dataUtil.clone(savedData.sampleSets);
                this.lastGenerate = Dyn.dataUtil.clone(savedData.lastGenerate);
                hasSample = true;
            }

            // apply filters
            if( hasSample ){
                this.applySamplingChange();
            } else {
                this.resetRandomSampling();
            }
        }

        /**
         * Get a unique ID for this configuration of sampled cases, so clients know when sample or population has changed.
         * @returns {number}
         */
        getID() {
            return this.samplerSerialNum;
        }

        getSelectedSampleIndex() {
            return this.sample.sampleIndex;
        }

        /**
         * true if samples have been generated, until they are reset again
         * Note: consider case of a sample set with zero samples: is this allowed? (no for now)
         */
        hasSamples() {
            return this.sampleSets.length > 0;
        }

        getLastSampleSet() {
            if( this.hasSamples()) {
                return this.sampleSets[ this.sampleSets.length - 1 ];
            } else {
                return { samples:[] };
            }
        }

        /**
         * @returns {boolean} - true if dataset+plot have sample cases, in sampling mode, as set up by applySamplingChange()
         */
        isSampleSelected() {
            return this.hasSamples() && this.selectedView === 'viewSample';
        }
        /**
         * @returns {boolean} - true if dataset+plot have population cases, in sampling mode, as set up by applySamplingChange()
         */
        isPopulationSelected() {
            return this.hasSamples() && this.selectedView === 'viewPopulation';
        }
        /**
         * @returns {boolean} - true if dataset+plot have distribution cases, in sampling mode, as set up by applySamplingChange()
         */
        isDistributionSelected() {
            return this.hasSamples() && this.selectedView === 'viewDistribution';
        }
        getViewKey() {
            // return an object key depending on what we are viewing in sample mode
            return this.hasSamples() ? this.selectedView : "notSampling";
        }

        /**
         * Clear all samples and revert to the population data
         * @param args {*} -- optional args for applySamplingChange
         */
        resetRandomSampling( args ) {

            // clear the current sample data
            this.sample.reset();
            this.sampleSets = [];

            // restore attribute's population case filter and data and notify clients
            this.applySamplingChange("reset", args);

            // clear the references to population data and filter now that it has been re-applied
            // (do after applySamplingChanges() which the pop data to reset)
            this.pop.reset();
            this.dist.reset();
            Dyn.assert(!this.hasSamples());
        }

        getGenerateSpeed( sampleCount ) {
            return {
                // ms: millisecond pause between generating samples, to show progress meter
                ms: Math.round(Dyn.mathUtil.clipToMinMax(0.1, 500, Math.pow(10, 4 - Math.log10(sampleCount)))),
                msAccel: 0.8,   // must be <=1 and > 0 to reduce time between samples gradually

                // n: number of samples to generate at a time between pauses, see generateSomeSamples()
                n: Dyn.mathUtil.clipToMinMax(0.1, 1000, Math.log(sampleCount) / Math.log(10)),
                nAccel: 1.1     // must be >=1 and > 0 to increase number of samples per update gradually
            }
        }

        get kChangeViewTimeMS() { return 100; }
        get kApplySamplesTimesMS() { return 100; }

        /**
         * Generate random samples, by creating or resetting the sample.sampleSets array,
         * with the given sample size.  A sample size of 0 clears the filter.
         * @param userInput
         * @param {number} userInput.sampleSize -- number of cases to include in filter (numeric positive)
         */
        generateRandomSamples( userInput ) {
            if( this.debug ) console.log("caseSampler:generateRandomSamples");

            // get the dataset cases
            let addToSamples = false, // future option to add to existing cases instead of regenerating new cases
                withReplacement = userInput.sampleType !== "without-replace",
                sampleSize = userInput.sampleSize, // note: actual number of samples may be less if withReplacement==false
                sampleCount = userInput.sampleCount,
                lastSetIndex = -1,
                lastSet = -1,
                populationCaseIndices = [],
                speed = this.getGenerateSpeed( sampleCount );

            if( sampleSize > 0 && sampleCount > 0 ){
                // generate all samples, with progress indicator
                this.taskMaster.start( speed.ms, () => { return generateNSamplesAndRepeat.call(this); } );
            } else {
                this.taskMaster.cancelLastTask();
                this.resetRandomSampling();
            }

            function savePopulationPreGenerate() {
                if( this.debug ) console.log("caseSampler:savePopulationPreGenerate");
                if( this.hasSamples() ){
                    // restore the case data for each attribute to the population
                    this.dataSet.attribs.forEach((attribute) => {
                        attribute.restorePopulationData(this);
                    });
                    // restore the population filter so we are sampling from the same cases as the first time.
                    this.dataSet.caseFilter.restore(this.pop.savedFilter, true /* defer plot update */);
                } else {
                    this.pop.savedFilter = this.dataSet.caseFilter.save();
                    this.pop.savedPlotTitle = Dyn.plotView.topMargin.getTitle();
                }
                this.pop.caseIndices = this.dataSet.caseFilter.getCasesAfterFilter();
                this.pop.numCasesPreFilter = this.dataSet.getNumCasesPreFilter();
                this.pop.numCasesAfterFilter = this.dataSet.getNumCasesAfterFilter();
                Dyn.assert(this.pop.caseIndices.length === this.pop.numCasesAfterFilter);
            }
            function initSampleSet() {
                if( this.debug ) console.log("caseSampler:initSampleSet");
                if( !addToSamples ){
                    this.sampleSets = [];
                    // this.sampleSets.splice(0, this.sampleSets.length);
                }
                this.setUserDataFromLastGenerate(userInput); // save for this.getUserDataFromLastGenerate();

                // push one sample set per Generate call
                this.sampleSets.push({
                    'name': userInput.sampleName,
                    'type': userInput.sampleType, // with our without replacement
                    'size': userInput.sampleSize,  // how many cases in each sample
                    'count': userInput.sampleCount, // how many samples to draw
                    'samples': []
                });
                lastSetIndex = this.sampleSets.length - 1;
                lastSet = this.sampleSets[lastSetIndex];
                populationCaseIndices = this.pop.caseIndices;
            }
            function generateOneSample() {
                if( this.debug ) console.log("caseSampler:generateOneSample");
                let caseIndices = [],
                    sampleIndex = lastSet.samples.length + 1;
                if( withReplacement ){
                    caseIndices = Dyn.dataUtil.randomPickArray(populationCaseIndices, sampleSize);
                } else {
                    caseIndices = Dyn.dataUtil.randomShuffleArray(populationCaseIndices, sampleSize);
                }
                // push one object per sample, until lastSet.caseIndices.length === sampleCount
                lastSet.samples.push({
                    'name': userInput.sampleName,
                    'id': Dyn.plotUtil.slugify(userInput.sampleName) + '-' + sampleIndex.toString(),
                    'caseIndices': Dyn.dataUtil.clone(caseIndices) // TODO: use Array.from(caseIndices)
                });
                // console.log("generateOneSample index", sampleIndex, "size", sampleSize );
            }
            function applyGeneratedSamples() {
                this.sample.setIndex = lastSetIndex;
                this.sample.sampleIndex = 0; // Select first sample
                if( this.debug ) console.log("caseSampler:applyGeneratedSamples index", this.sample.setIndex, this.sample.sampleIndex );
                Dyn.assertArrayIndex( this.sampleSets, this.sample.setIndex );
                Dyn.assertArrayIndex( this.sampleSets[this.sample.setIndex].samples, this.sample.sampleIndex );
                this.sample.caseIndices = this.sampleSets[this.sample.setIndex].samples[this.sample.sampleIndex].caseIndices;
                this.sample.numCasesPreFilter = this.sample.caseIndices.length;
                // if( userInput.selectedView ) {
                //     this.selectedView = userInput.selectedView;
                // }
                this.applySamplingChange("generate", {
                    selectedView: userInput.selectedView,
                    statsChecked: userInput.statsChecked,
                    statsUnchecked: userInput.statsUnchecked
                });
            }
            function generateNSamplesAndRepeat() {
                if( this.debug ) console.log("caseSampler:generateNSamplesAndRepeat");
                if( lastSetIndex === -1 ) {
                    // if not initialized, create the sample set before generating the first sample
                    savePopulationPreGenerate.call( this );
                    initSampleSet.call( this );
                    Dyn.assert( this.hasSamples());
                }
                // console.log( "generateNSamplesAndRepeat() "+lastSet.samples.length+" of "+sampleCount, speed.ms, Math.round( (10*speed.n)/10 ));

                if( this.hasSamples() && ! needMoreSamples()) {
                    // show that last sample was generated and update dataset and plot
                    showGenerateProgress( lastSet.samples.length );
                    return this.taskMaster.newTaskStep( applyGeneratedSamples, this.kApplySamplesTimesMS );
                }
                else if( this.hasSamples() ) {
                    // add a batch of samples to last sample set
                    // batch size is Math.ceiling(speed.n)
                    showGenerateProgress( lastSet.samples.length+1 );
                    for( let i = 0; (i < speed.n) && needMoreSamples(); i++ ) {
                        generateOneSample.call( this );
                    }
                    // decrease the time between batches, and increase the batch size
                    speed.ms = speed.ms * speed.msAccel; // ok if non-integer
                    speed.n = speed.n * speed.nAccel; // ok if non-integer
                    // repeat after speed.ms delay; this will be the last time if needMoreSamples()===false
                    return this.taskMaster.newTaskStep( generateNSamplesAndRepeat, speed.ms );
                }
                // else user canceled or closed sampling panel
                return null;
            }
            function needMoreSamples() {
                return lastSet.samples.length < sampleCount;
            }
            function showGenerateProgress( count ) {
                Dyn.mediator.publish("plot:indicateProgress", {
                    message: Dyn.loc("Generating Sample {{n}}", {n:count})
                });
            }
        }

        /**
         * Apply the changes to the dataset, and inform other components.
         * @param changeHint {string} - type of change, if we need to know
         * @param args {*}
         * @param args.dontRestorePopulationFilter {boolean} - if true, don't restore the saved population filter on reset
         * @param args.deferPlotUpdate {boolean} - if true, don't update the plot yet, to avoid redundant updates.
         * @param args.statsChecked {Array} -- array of checked statistic names to apply to plot
         * @param args.statsUnchecked {Array} -- array of unchecked statistic names to remove from plot
         * @param args.selectedView {string} -- new view type, if any
         */
        applySamplingChange( changeHint, args ) {

            let isReset = (this.sampleSets.length === 0),
                isSelectSample = (changeHint === "viewSample" && !isReset),
                isSelectDistribution = (changeHint === "viewDistribution" && !isReset),
                isSelectPopulation = (changeHint === "viewPopulation" && !isReset),
                isSelect = (isSelectSample || isSelectDistribution || isSelectPopulation),
                isGenerate = (this.sampleSets.length >= 0 && !(isReset || isSelect));
                //isGenerateToDistribution = ( isGenerate && this.selectedView==='viewDistribution'),
                //isGenerateToPopulation = ( isGenerate && this.selectedView==='viewPopulation');
            args = args || {};
            Dyn.assert(1 === [isReset + isSelectSample + isSelectDistribution + isSelectPopulation + isGenerate].reduce((a, e) => a + e),
                "unknown change, expecting reset, select, or generate samples");
            let caseSelection = this.mapCaseSelection( this.selectedView, (isSelect ? changeHint : this.selectedView));

            // increment the serial number when samples have changed (not when viewing a new sample already generated)
            if( isGenerate || isReset ){
                this.samplerSerialNum++;
            }
            // set the new view
            if( isReset ) {
                this.selectedView = "";
                if( this.debug ) console.log("caseSampler:applySamplingChange() reset =====");
            } else if( isSelect ) {
                this.selectedView = changeHint;
                if( this.debug ) console.log("caseSampler:applySamplingChange() select ====== ", this.selectedView );
            } if( isGenerate ) {
                if(  args.selectedView==='viewPopulation'||
                     args.selectedView==='viewDistribution') {
                    this.selectedView = args.selectedView;
                } else {
                    this.selectedView = 'viewSample'; // default if we need a view
                }
                if( this.debug ) console.log("caseSampler:applySamplingChange() Generate ===== ", this.selectedView );
            }
            Dyn.assert( isReset || this.isPopulationSelected() || this.isDistributionSelected() || this.isSampleSelected());

            // update the minimum of sample stats needed to create our distribution data
            mediator.publish("records:randomSamplesPre-Update",  {
                    sampleSets: this.sampleSets,
                    statsChecked: args.statsChecked
                    // statsUnchecked: args.statsUnchecked
                });
            // NOTE: this in turn may call our updateDistribution() method via notification

            // dataSet are next: update case filters, attribute cases, attribute stats
            if( isReset ){
                // restore attribute cases and stats to population
                this.dataSet.attribs.forEach((attribute) => {
                    attribute.restorePopulationData(this);
                });
                // restore original data set filter, pre-Generate (unless asked not to, e.g. for Plot Reset)
                this.dataSet.caseFilter.restore(
                    ( this.pop.savedFilter && !(args.dontRestorePopulationFilter)) ? this.pop.savedFilter : {} );
            } else {
                Dyn.assert(isSelect || isGenerate);
                // save the population for restore, if not yet saved (if on first Generate)
                if( isGenerate ) {
                    this.dataSet.attribs.forEach((attribute) => {
                        attribute.savePopulationData(this);
                    });
                }
                // update attribute cases and stats (filter not yet correct, so ignore stats until next step)
                this.dataSet.attribs.forEach((attribute) => {
                    attribute.updateSampleCases(this, this.selectedView); // update attribute cases and stats
                });
                // clear or restore the filter; meaning of case indices change between viewing Pop/Sample/Dist, so filters must be cleared
                if( this.isPopulationSelected()) {
                    // restore dataset's original filter (saved on first generate) if viewing the population
                    this.dataSet.caseFilter.restore( this.pop.savedFilter || {} ); // restore pop filter before generating/viewing samples
                } else {
                    // clear dataset's filter for viewing samples or distributions; they have different case indices than population
                    this.dataSet.caseFilter.resetAll();
                }
            }

            // clear the case selection since it no longer applies, defer plot update
            mediator.publish( "records:casesSelected", caseSelection);

            // update the dataset and table with name and case changes
            let selectedSample = this._getSelectedSample(),
                publishData = {
                    sampleSets: this.sampleSets,
                    attributeCasesChanged: true,
            };
            if( this.allowChangeToAttributeNames ){
                publishData.attributeNamesChanged = this.dataSet.updateMetaDataNamesFromAttributes();
            }

            // publish message to update the plot, case card, case table, etc
            if( selectedSample ){
                publishData.selectedView = this.selectedView;
                publishData.setIndex = this.sample.setIndex;
                publishData.sampleIndex = this.sample.sampleIndex;
                publishData.sampleName = selectedSample.name;
                publishData.plotTitle = this.getPlotTitleForSelectedSample(
                    publishData.sampleName,
                    publishData.sampleIndex + 1,
                    this.lastGenerate.statsChecked );
                publishData.isGenerate = isGenerate;
                if( args.statsChecked ){
                    publishData.statsChecked = args.statsChecked;
                }
                if( args.statsUnchecked ){
                    publishData.statsUnchecked = args.statsUnchecked;
                }
            }
            if( isReset ){
                publishData.plotTitle = this.pop.savedPlotTitle;
            }
            if( args.deferPlotUpdate ){
                publishData.deferPlotUpdate = true; // e.g. if reset is part of mixup
            }
            mediator.publish("records:randomSamplesUpdated", publishData);

            // in order to regenerate while viewing a distribution or population, we must:
            //  (a) generate with selectedView==='viewSample' (which is what we did above)
            //  (b) now we must select the distribution and republish.
            // if( isGenerateToDistribution ) {
            //     this.applySamplingChange("viewDistribution");
            // } else if( isGenerateToPopulation ) {
            //     this.applySamplingChange("viewPopulation");
            // }
        }

        /** change the view after showing the progress indicator for some time */
        showViewChangeWithProgress( newViewType, progressType ) {
            // console.log("showViewChangeWithProgress() start");
            if( this.hasSamples()){
                Dyn.mediator.publish("plot:indicateProgress", { prefix: progressType+" " });
                this.taskMaster.start(
                    this.kChangeViewTimeMS,
                    () => {
                        // console.log("showViewChangeWithProgress() finish");
                        if( this.hasSamples()){
                            this.applySamplingChange( newViewType );
                        } else {
                            Dyn.mediator.publish( "plot:indicateProgress", { progress: -1 });
                        }
                        return null; // end of task
                    });
            }
        }

        /**
         * viewSample : respond to "records:viewSample" to select and view one of the generated samples (in the plot/card/table).
         * @param args {*} -- args obj, if no parameters it select the last-viewed sample (e.g. switching from view Pop. to view sample).
         * @param args.sampleID {string} -- optional ID to match to this.sampleSets[].samples.id;
         * @param args.sampleIndex {number} -- optional index 0+ to match
         */
        viewSample( args ) {
            if( this.hasSamples() ){
                let oldSampleIndex = this.sample.sampleIndex,
                    oldSetIndex = this.sample.setIndex,
                    oldSelectType = this.selectedView,
                    newSetIndex = (args && (args.setIndex >= 0)) ? args.setIndex : this.sampleSets.length - 1,
                    newSetObj = this.sampleSets[newSetIndex],
                    newSampleIndex = (args && (args.sampleIndex >= 0)) ? args.sampleIndex : oldSampleIndex,
                    newSelectType = 'viewSample';
                if( Dyn.mathUtil.isInIntegerRange(newSetIndex, 0, this.sampleSets.length) &&
                    Dyn.mathUtil.isInIntegerRange(newSampleIndex, 0, newSetObj.samples.length) &&
                    (oldSetIndex != newSetIndex || oldSampleIndex !== newSampleIndex || oldSelectType !== newSelectType) ){
                    // valid change to selected sample, now change it
                    this.sample.setIndex = newSetIndex;
                    this.sample.sampleIndex = newSampleIndex;
                    this.sample.caseIndices = this.sampleSets[newSetIndex].samples[newSampleIndex].caseIndices;
                    this.sample.numCasesPreFilter = this.sample.caseIndices.length;
                    this.showViewChangeWithProgress( "viewSample","Sample "+(newSampleIndex+1));
                }
            }
        }

        /** respond to "records:viewDistribution" to view the distribution of samples as the current plot. */
        viewDistribution(args) {
            // console.log("records:viewDistribution", args);
            this.showViewChangeWithProgress( "viewDistribution", Dyn.loc("Distribution"));
        }

        /** respond to "records:viewPopulation" to view the distribution of samples as the current plot. */
        viewPopulation(args) {
            // console.log("records:viewPopulation", args);
            this.showViewChangeWithProgress( "viewPopulation", Dyn.loc("Population"));
        }

        /** return the index of the currently selected sample set and sample */
        getSampleIndex() {
            return ({
                setIndex: this.sample.setIndex,
                sampleIndex: this.sample.sampleIndex
            });
        }

        /** return the currently selected sample, or null */
        _getSelectedSample() {
            let isSetInRange = Dyn.mathUtil.isInIntegerRange(this.sample.setIndex, 0, this.sampleSets.length);
            if( isSetInRange ){
                let sampleSet = this.sampleSets[this.sample.setIndex],
                    isSampleInRange = Dyn.mathUtil.isInIntegerRange(this.sample.sampleIndex, 0, sampleSet.samples.length);
                if( isSampleInRange ){
                    return sampleSet.samples[this.sample.sampleIndex];
                }
            }
            return null;
        }

        getNumCasesInSample() {
            "use strict";
            let sample = this._getSelectedSample();
            return (sample ? sample.caseIndices.length : 0);
        }

        /**
         * Return the parameters from the last call to generateRandomSamples() that produced a random sample.
         * Used to restore the panel after close then re-open.
         * Parameters are entirely up to caller (RandomSampleComponent.jsx)
         */
        getUserDataFromLastGenerate() {
            return this.lastGenerate;
        }
        setUserDataFromLastGenerate(data) {
            Dyn.assert(typeof data === "object");
            if( data ){
                this.lastGenerate = Dyn.dataUtil.clone(data); // save for this.getUserDataFromLastGenerate();
            } else {
                this.lastGenerate = {}; // clear the settings if null
            }
        }

        getPlotTitleForSelectedSample(sampleName, sampleNumber, statsChecked ) {
            switch(this.selectedView){
                case 'viewPopulation':
                    return Dyn.loc("Population");
                case 'viewDistribution': {
                    switch(statsChecked[0]) {
                        // may be unknown, compare to averageElement.js view.nameOfAverage
                        case "kControlsMean":
                            return Dyn.loc("Distribution of Sample Means");
                        case "kControlsStDeviation":
                            return Dyn.loc("Distribution of Sample Std. Deviations");
                        case "kControlsVariance":
                            return Dyn.loc("Distribution of Sample Variances");
                        case "kControlsMedian":
                            return Dyn.loc("Distribution of Sample Medians");
                        case "kControlsInterQuartRange":
                            return Dyn.loc("Distribution of Sample IQRs");
                        default:
                            return Dyn.loc("Distribution of Samples");
                    }
                }
                case 'viewSample':
                default:
                    return Dyn.loc("{{name}} {{number}}", {name: sampleName, number: sampleNumber});
            }
        }

        /** get the number of derived attributes created by statistics of the samples -- e.g. mean of weight = 1 attribute */
        getNumAttributesInDistribution() {
            return this.dist.attributes.length; // updated by this.updateDistribution()
        }

        /** get the number of case attributes created by statistics of the samples -- e.g. mean of weight for 4 samples = 4 cases */
        getNumCasesInDistribution() {
            return this.dist.numCasesInDistribution;
        }

        /**
         * Update this.dist to match our sampled cases and attributes.  Because of order of operations,
         * and some of the data is generated from changes to the plot (e.g. another plot statistic added)
         * this.dist may be updated at a later stage than other changes in applySamplingChange().
         * @param samplerData
         */
        updateDistribution(args) {
            // get a copy of the new distribution data
            let sampleStats = args.sampleStats,
                numAttributes = sampleStats.getNumAttributesInDistribution(),
                numCases = sampleStats.getNumCasesInDistribution(),
                newAttrData = [],
                changed = false;
            // console.log("CaseSampler.updateDistribution() with "+numAttributes+" attrs. and "+numCases+" cases.");
            for( let i = 0; i < numAttributes; i++ ){
                let d = sampleStats.getDistributionAttributeData(i);
                if( d ){
                    newAttrData.push(d);
                }
            }

            // see if distribution data table has changed in value or length since last update
            if( newAttrData.length !== this.getNumAttributesInDistribution() ){
                changed = true;
            } else if( numCases !== this.getNumCasesInDistribution() ){
                changed = true;
            } else {
                // compare data values to see if anything has changed
                changed = Boolean(this.dist.attributes.find(
                    (oldAttrData, i) =>
                        sampleStats.isDistributionDataChanged(oldAttrData, newAttrData[i])));
            }
            if( changed) {
                // update this.dist
                this.dist.attributes = newAttrData;
                this.dist.numCasesInDistribution = numCases;
                ++this.distSerialNum;
                if( this.debug ) console.log("CaseSampler.updateDistribution() with "+numAttributes+" attrs. and "+numCases+" cases.");
            }
        }

        /**
         * Find distribution data vales that match the given attribute data.
         * For example if given id of attribute weight, returns the 7 sample means of weight,
         * as computed by updateDistribution.
         * Note: to be called after updateDistribution to give correct results.
         * @param originalAttributeID
         * @returns {*}
         */
        findDistributionAttributeData(originalAttributeID) {
            // just find the first distribution stats that match the attribute,
            // ignoring other stat that might also match,
            // and return null if no matching data is found.
            return this.dist.attributes.find((attrData) => attrData.id === originalAttributeID);
        }

        /**
         * Get the new selected case IDs to be used for applySamplingChange().
         * @param oldView
         * @param newView
         * @returns {{ids: [], deferPlotUpdate: boolean, object: string}
         */
        mapCaseSelection( oldView, newView ) {
            let caseIDs = [];
            if( newView === 'viewDistribution') {
                // clear selection, no correspondence between pop. and sample selected cases.
            } else if( oldView === 'viewPopulation' && newView === 'viewSample' ) {
                // TODO: map population selected cases to current sample cases
                // For now we just clear the selected cases.
            } else if( oldView === 'viewSample' && oldView === 'viewPopulation' ) {
                // TODO: map current sample cases to population cases
                // For now we just clear the selected cases.
            } else if( oldView === 'viewSample' && oldView === 'viewSample' ) {
                // TODO: map old sample cases to new new sample cases
                // For now we just clear the selected cases.
            }
            return { ids:caseIDs, object:"caseSampler", deferPlotUpdate:true };
        }

    } // class CaseSampler
    Dyn.CaseSampler = CaseSampler;

    class SamplerTaskMaster {
        /**
         * Sampler task handler, performs tasks for the case sampler, with small time interals
         * between tasks so that the progress bar can show what is happening.
         * Guarantees that any task started will complete before the next task is performed.
         * @param caseSampler
         */
        constructor( caseSampler ) {
            this.caseSampler = caseSampler;
            this.nextTask = null; // function to perform
            this.timerID = null;
        }

        isRunning() {
            Dyn.assert( this.nextTask === null || (typeof this.nextTask === 'function'));
            return( this.nextTask !== null );
        }

        /** Start a new single or multi-step task. Guarantees that the task will run
         * before any new tasks are started.
         * @param delayInMS
         * @param taskFunction
         */
        start( delayInMS, taskFunction ) {
            let taskMaster = this;
            this.finishLastTask();
            this.nextTask = taskFunction;
            this.timerID = setTimeout(() => {
                // console.log("SamplerTaskMaster.start -- run task");
                let result = taskMaster.nextTask(); // run this task
                taskMaster.timerID = taskMaster.nextTask = null; // mark task as done
                if( result && result.delayInMS && result.nextTask ) {
                    taskMaster.start( result.delayInMS, result.nextTask); // queue up next one
                }
            }, delayInMS);
        }

        /** constructor of a next step for a series of tasks.  the taskFunction should
         * return this newTaskStep to create the next step, or return null to end the task.
         * @param taskFunction
         * @param delayInMS
         * @returns {{delayInMS, nextTask: (function(): *)}}
         */
        newTaskStep( taskFunction, delayInMS ) {
            return {
                nextTask: ()=>{ return taskFunction.call( this.caseSampler ); },
                delayInMS: delayInMS
            };
        }

        /** finish the last task quickly, if any, without delays */
        finishLastTask() {
            if( this.isRunning()) {
                // console.log("SamplerTaskMaster.finishTaskNow", this.timerID );
                // clear timer
                clearTimeout(this.timerID);
                // run tasks quickly until done
                for( let n = this.nextTask; n; ) {
                    console.log("SamplerTaskMaster.finishTaskNow -- run task");
                    let result = n();
                    n = (result && result.nextTask) || null;
                }
                this.timerID = this.nextTask = null;
            }
            Dyn.assert( ! this.isRunning());
        }

        cancelLastTask() {
            if( this.isRunning()){
                // console.log("SamplerTaskMaster.cancelLastTask", this.timerID);
                // clear timer
                clearTimeout(this.timerID);
                this.timerID = this.nextTask = null;
            }
        }
    } // class SamplerTaskMaster
    Dyn.SamplerTaskMaster = SamplerTaskMaster;

} // wrapDynCaseSampler



